import { useEffect, useState } from 'react';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import _ from 'lodash';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Promise } from 'bluebird';

import { Button, Modal, Upload, Menu, Radio, Alert } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { cgp, general } from './data/csvFormat';
import { webServiceProvider } from 'shared';
import { AppStore, ShopStore, CurrentUserStore } from 'stores';
import { SelectCars } from 'containers/Users';

const StyledModal = styled(Modal)`
  .ant-modal-header {
    padding: 0px;
  }
`;

const UploadCsvForm = () => {
  const [state, setState] = useState({
    selectedCarIds: [],
    csvFiles: [],
    option: 'general',
    odoUnits: CurrentUserStore.user.displayedOdometer,
    dateFormat: 'DD/MM/YYYY',
    wrongFileUploads: [],
    isWrongFileUploadsVisible: false,
    data: null,
  });

  useEffect(() => {
    const disposer = observe(ShopStore.currentShop, 'id', () => {
      setState((prevState) => ({
        ...prevState,
        selectedCarIds: [],
      }));
    });

    return () => disposer();
  }, []);

  const onSelect = (ids) => {
    setState((prevState) => ({
      ...prevState,
      selectedCarIds: ids,
    }));
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;

    reader.onload = (e) => {
      const bstr = e.target.result;
      const wb = XLSX.read(bstr, { type: rABS ? 'binary' : 'array' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
      setState((prevState) => ({
        ...prevState,
        data,
      }));
    };

    if (rABS) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const getFileName = (fileName) => {
    let strArr = _.split(fileName, '.');
    return {
      name: `${moment().format('YYYY-MM-DD')}_${_.first(strArr)}`,
      extension: _.last(strArr),
    };
  };

  const uploadFile = async (file) => {
    let files = [];

    let blob = new Blob([file.originFileObj], {
      type: 'application/vnd.ms-excel',
    });

    let _file = new File(
      [blob],
      `${getFileName(file.name).name}.${getFileName(file.name).extension}`,
      {
        type: 'application/vnd.ms-excel',
      }
    );

    files.push(_file);

    if (getFileName(file.name).extension === 'xlsx') {
      const ws = XLSX.utils.aoa_to_sheet(state.data);
      let csvFile = XLSX.utils.sheet_to_csv(ws);

      let blob = new Blob([csvFile], {
        type: 'application/vnd.ms-excel',
      });

      let _file = new File([blob], `${getFileName(file.name).name}.csv`, {
        type: 'application/vnd.ms-excel',
      });

      files.push(_file);
    }

    await Promise.try(async () => {
      let { option, selectedCarIds, dateFormat, odoUnits } = state;

      await Promise.map(files, async (file) => {
        const fd = new FormData();
        fd.append('singleInputFileName', file);

        if (option !== 'general') {
          fd.append('whichFormatter', option);
        }

        fd.append('dateFormat', dateFormat);
        fd.append('odoUnits', odoUnits);

        AppStore.addLoading('Uploading CSV file...');

        let result = await webServiceProvider.postFormData(
          `v1/${selectedCarIds[0]}/serviceHistory`,
          fd
        );

        if (!result.success) {
          let wrongFileUploads = _.map(
            _.slice(result.result.details, 0, 5),
            (detail) => {
              let keys = Object.keys(detail.errors);
              let message = [];

              _.forEach(keys, (key) => {
                message.push(
                  `Row: ${Number(detail.row) + 1} - Column: ${key} - Issue: ${
                    detail.errors[key].issue
                  } - Valid Type: ${detail.errors[key].validType}`
                );
              });

              return message;
            }
          );

          setState((prevState) => ({
            ...prevState,
            wrongFileUploads,
            isWrongFileUploadsVisible: true,
          }));

          throw Error('Wrong File Upload!');
        }
      });
    })
      .catch((err) => {
        if (err.message === 'Wrong File Upload!')
          throw Error('Wrong File Upload!');
        throw Error('Sorry! Uploading CSV files has failed!');
      })
      .finally(() => AppStore.removeLoading('Uploading CSV file...'));
  };

  const beforeUpload = async (file) => {
    handleFile(file);
  };

  const handleChange = (info) => {
    let fileList = [...info.fileList];

    if (_.isEmpty(fileList)) {
      setState((prevState) => ({
        ...prevState,
        csvFiles: [],
      }));
    } else {
      fileList = [_.last(fileList)];
      fileList = _.map(fileList, (file) => {
        if (file.response) {
          file.url = file.response.url;
        }
        return file;
      });

      setState((prevState) => ({
        ...prevState,
        csvFiles: fileList,
      }));
    }
  };

  const handleRemove = (file) => {
    const csvFiles = state.csvFiles.filter((f) => f.uid !== file.uid);
    setState((prevState) => ({
      ...prevState,
      csvFiles,
    }));
  };

  const onPreview = (file) => {
    if (file && file.originFileObj) {
      var blob = new Blob([file.originFileObj], {
        type: 'application/vnd.ms-excel',
      });
      saveAs(blob, file.originFileObj.name);
    }
  };

  const dummyRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const sendCSVFiles = async () => {
    let { csvFiles, selectedCarIds } = state;

    try {
      if (ShopStore.currentShop.id === -1) {
        throw Error('Please select another shop!');
      }

      if (_.isEmpty(csvFiles)) {
        throw Error('Please upload CSV files!');
      }

      if (_.isEmpty(selectedCarIds)) {
        throw Error('Please select vehicle!');
      }

      await uploadFile(csvFiles[0]);
      AppStore.addSuccess('Uploading CSV files done!');
    } catch (err) {
      if (err.message !== 'Wrong File Upload!') AppStore.addError(err.message);
    }
  };

  return (
    <>
      <Upload
        customRequest={dummyRequest}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        onRemove={handleRemove}
        multiple={false}
        accept=".csv, .xlsx"
        onPreview={onPreview}
        fileList={state.csvFiles}
      >
        <Button>
          <UploadOutlined /> Upload CSV
        </Button>
      </Upload>

      <div style={{ margin: '10px 0px' }}>
        <b>
          <p>Select Odometer Unit</p>
        </b>
        <div>
          <Radio.Group
            onChange={(e) =>
              setState((prevState) => ({
                ...prevState,
                odoUnits: e.target.value,
              }))
            }
            defaultValue={state.odoUnits === 'km' ? 'k' : 'm'}
          >
            <Radio value="k">km (Kilometer)</Radio>
            <Radio value="m">m (Mile)</Radio>
          </Radio.Group>
        </div>
      </div>

      <div style={{ margin: '10px 0px' }}>
        <b>
          <p>Select Date Format</p>
        </b>
        <div>
          <Radio.Group
            onChange={(e) =>
              setState((prevState) => ({
                ...prevState,
                dateFormat: e.target.value,
              }))
            }
            defaultValue="MM/DD/YYYY"
          >
            <Radio value="MM/DD/YYYY">MM/DD/YYYY</Radio>
            <Radio value="DD/MM/YYYY">DD/MM/YYYY</Radio>
          </Radio.Group>
        </div>
      </div>

      <div style={{ margin: '10px 0px' }}>
        <b>
          <p>Select file formatter:</p>
        </b>
        <Menu
          onClick={(e) =>
            setState((prevState) => ({
              ...prevState,
              option: e.key,
            }))
          }
          selectedKeys={[state.option]}
          mode="horizontal"
        >
          <Menu.Item key="general">
            <p>Option 1</p>
          </Menu.Item>
          <Menu.Item key="CGP">
            <p>Option 2</p>
          </Menu.Item>
        </Menu>

        {state.option === 'general' && (
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                {_.map(_.keys(general[0]), (key, i) => (
                  <th key={i}>{key}</th>
                ))}
              </tr>
              {_.map(general, (record, i) => (
                <tr key={i}>
                  {_.map(record, (rec, i) => (
                    <td key={i}>{rec}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {state.option === 'CGP' && (
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                {_.map(_.keys(cgp[0]), (key, i) => (
                  <th key={i} style={{ padding: 2 }}>
                    {key}
                  </th>
                ))}
              </tr>
              {_.map(cgp, (record, i) => (
                <tr key={i}>
                  {_.map(record, (rec, i) => (
                    <td key={i} style={{ padding: 2 }}>
                      {rec}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <b>
        <p>Select One Vehicle</p>
      </b>
      <SelectCars
        rowSelection={{
          selectedCarIds: state.selectedCarIds,
          onChange: onSelect,
          type: 'radio',
        }}
      />

      <Button
        style={{ marginTop: '10px' }}
        type="primary"
        onClick={sendCSVFiles}
      >
        Upload files
      </Button>

      <StyledModal
        title={
          <Alert
            type="error"
            message="Error"
            description="Please fix issues with interface type: RepairOrder"
            banner
          />
        }
        open={state.isWrongFileUploadsVisible}
        onOk={() =>
          setState((prevState) => ({
            ...prevState,
            isWrongFileUploadsVisible: false,
          }))
        }
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {_.map(_.flatten(state.wrongFileUploads), (fail) => (
          <p key={fail}>{fail}</p>
        ))}
      </StyledModal>
    </>
  );
};

export default observer(UploadCsvForm);
